import React, { useState, useEffect } from 'react';
import { IoDocumentTextOutline, IoTimeOutline, IoCheckmarkCircleOutline, IoCloseCircleOutline, IoHourglassOutline, IoChevronBackOutline, IoChevronForwardOutline } from 'react-icons/io5';
import toast from 'react-hot-toast';
import api from '../services/api';
import { useTranslation } from 'react-i18next';

export default function CryptoWithdrawalsContent() {
    const { t } = useTranslation();
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [withdrawals, setWithdrawals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // API'den veri çekme
    useEffect(() => {
        fetchCryptoWithdrawals();
    }, []);

    const fetchCryptoWithdrawals = async () => {
        try {
            setLoading(true);
            setError(null);

            const result = await api.getCryptoWithdrawals();

            if (result.success) {
                setWithdrawals(result.data || []);
            } else {
                throw new Error(result.message || t('cryptoWithdrawalsContent.dataFetchError'));
            }
        } catch (err) {
            console.error('API Error:', err);
            setError(err.message || t('cryptoWithdrawalsContent.errorLoadingData'));
            toast.error(t('cryptoWithdrawalsContent.errorLoadingData'));
        } finally {
            setLoading(false);
        }
    };

    // Tarih formatını düzenleme
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString('tr-TR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    // API'den gelen veriye göre durum belirleme (API'de status yok, hepsini completed olarak göster)
    const getStatusIcon = () => {
        return <IoCheckmarkCircleOutline size={20} className="text-success" />;
    };

    const getStatusText = () => {
        return t('cryptoWithdrawalsContent.status.completed');
    };

    const getStatusClass = () => {
        return 'status-completed';
    };

    const totalPages = Math.ceil(withdrawals.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentData = withdrawals.slice(startIndex, endIndex);

    return (
        <div className="crypto-withdrawals-content">
            <div className="change-password-header">
                <h1>{t('cryptoWithdrawalsContent.title')}</h1>
                <p>{t('cryptoWithdrawalsContent.subtitle')}</p>
            </div>

            <div className="content-body">
                {loading ? (
                    <div className="loading-container">
                        <div className="spinner"></div>
                        <p>{t('cryptoWithdrawalsContent.loading')}</p>
                    </div>
                ) : error ? (
                    <div className="error-container">
                        <p>{t('cryptoWithdrawalsContent.error')} {error}</p>
                        <button onClick={fetchCryptoWithdrawals} className="retry-btn">
                            {t('cryptoWithdrawalsContent.retryButton')}
                        </button>
                    </div>
                ) : (
                    <div className="table-container">
                        {currentData.length === 0 ? (
                            <div className="no-data">
                                <div className="no-data-content">
                                    <IoDocumentTextOutline size={48} />
                                    <p>{t('cryptoWithdrawalsContent.noDataMessage')}</p>
                                </div>
                            </div>
                        ) : (
                            <div className="div-table">
                                {/* Header */}
                                <div className="div-table-header">
                                    <div className="div-table-cell">{t('cryptoWithdrawalsContent.tableHeaders.coin')}</div>
                                    <div className="div-table-cell">{t('cryptoWithdrawalsContent.tableHeaders.amount')}</div>
                                    <div className="div-table-cell">{t('cryptoWithdrawalsContent.tableHeaders.address')}</div>
                                    <div className="div-table-cell">{t('cryptoWithdrawalsContent.tableHeaders.transactionId')}</div>
                                    <div className="div-table-cell">{t('cryptoWithdrawalsContent.tableHeaders.status')}</div>
                                    <div className="div-table-cell">{t('cryptoWithdrawalsContent.tableHeaders.date')}</div>
                                </div>

                                {/* Body */}
                                <div className="div-table-body">
                                    {currentData.map((item) => (
                                        <div key={item.withdrawalId} className="div-table-row">
                                            <div className="div-table-cell" data-label={t('cryptoWithdrawalsContent.tableHeaders.coin')}>
                                                <div className="coin-info">
                                                    <span className="coin-symbol">{item.asset}</span>
                                                </div>
                                            </div>
                                            <div className="div-table-cell" data-label={t('cryptoWithdrawalsContent.tableHeaders.amount')}>
                                                <div className="amount-info">
                                                    <span className="amount">{item.amount}</span>
                                                </div>
                                            </div>
                                            <div className="div-table-cell" data-label={t('cryptoWithdrawalsContent.tableHeaders.address')}>
                                                <div className="address-info">
                                                    <span className="address">{item.walletAddress}</span>
                                                </div>
                                            </div>
                                            <div className="div-table-cell" data-label={t('cryptoWithdrawalsContent.tableHeaders.transactionId')}>
                                                <div className="tx-hash-info">
                                                    <span className="tx-hash">{item.withdrawalId}</span>
                                                </div>
                                            </div>
                                            <div className="div-table-cell" data-label={t('cryptoWithdrawalsContent.tableHeaders.status')}>
                                                <div className={`status-badge ${getStatusClass()}`}>
                                                    <span>{getStatusText()}</span>
                                                </div>
                                            </div>
                                            <div className="div-table-cell" data-label={t('cryptoWithdrawalsContent.tableHeaders.date')}>
                                                <div className="date-info">
                                                    <span className="date">{formatDate(item.createdDate)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="pagination-container">
                                {/* <div className="pagination-info">
                                    <span>
                                        {startIndex + 1}-{Math.min(endIndex, withdrawals.length)} / {withdrawals.length} {t('cryptoWithdrawalsContent.pagination.records')}
                                    </span>
                                </div> */}
                                <div className="pagination">
                                    <button
                                        className="pagination-btn prev-btn"
                                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                        disabled={currentPage === 1}
                                    >
                                        <IoChevronBackOutline size={18} />
                                    </button>

                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                        <button
                                            key={page}
                                            className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                                            onClick={() => setCurrentPage(page)}
                                        >
                                            {page}
                                        </button>
                                    ))}

                                    <button
                                        className="pagination-btn next-btn"
                                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                        disabled={currentPage === totalPages}
                                    >
                                        <IoChevronForwardOutline size={18} />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
